// The requests behind the warmup screens, as hooks. They ask again once a
// minute — the scheduler's own tick — so a pause made by the scheduler, the
// watchdog or a terminal shows up without anybody pressing reload. The last
// good answer stays on screen while the next one is on its way, and an answer
// that arrives after a newer one was asked for is dropped.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchContactSummary,
  fetchHandover,
  fetchOverview,
  fetchWarmupBoard,
  fetchWarmupDays,
  fetchWarmupEvents,
  fetchWarmupPool,
} from "@/lib/api";
import type {
  ContactSummary,
  Handover,
  Overview,
  WarmupBoard,
  WarmupDays,
  WarmupEvent,
  WarmupPool,
} from "@/lib/api";
import { messageOf } from "@/pages/campaigns/model";

export const POLL_MS = 60_000;
export const DETAIL_DAYS = 30;
export const EVENTS_SHOWN = 60;

function useLatest() {
  const counter = useRef(0);
  return useCallback(() => {
    counter.current += 1;
    const ticket = counter.current;
    return () => ticket === counter.current;
  }, []);
}

interface Polled<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /** When the data on screen was read. */
  at: Date | null;
  reload: () => Promise<void>;
}

function usePolled<T>(read: () => Promise<T>, key: string): Polled<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [at, setAt] = useState<Date | null>(null);
  const latest = useLatest();
  const reader = useRef(read);
  reader.current = read;

  const reload = useCallback(async () => {
    const current = latest();
    setLoading(true);
    try {
      const answer = await reader.current();
      if (!current()) return;
      setData(answer);
      setAt(new Date());
      setError(null);
    } catch (raised: unknown) {
      if (current()) setError(messageOf(raised));
    } finally {
      if (current()) setLoading(false);
    }
  }, [latest]);

  useEffect(() => {
    setData(null);
    void reload();
    const timer = window.setInterval(() => void reload(), POLL_MS);
    return () => window.clearInterval(timer);
    // `key` names what is being read; a new one starts from nothing.
  }, [reload, key]);

  return { data, error, loading, at, reload };
}

export function useWarmupBoard(): Polled<WarmupBoard> {
  return usePolled(fetchWarmupBoard, "board");
}

/** Every country's handover row: the country list and the handover panel read it. */
export function useHandoverRows(): Polled<Handover[]> {
  return usePolled(fetchHandover, "handover");
}

/** Each country's subscribed, present, not excluded contacts — the contacts
 * screen's figures, kept on the server for a few minutes. */
export function useContactSummary(): Polled<ContactSummary> {
  return usePolled(fetchContactSummary, "contact-summary");
}

export interface PoolDetailData {
  pool: WarmupPool;
  days: WarmupDays;
  events: WarmupEvent[];
}

export function usePoolDetail(code: string): Polled<PoolDetailData> {
  return usePolled(
    async () => {
      const [pool, days, events] = await Promise.all([
        fetchWarmupPool(code),
        fetchWarmupDays(code, DETAIL_DAYS),
        fetchWarmupEvents(code, EVENTS_SHOWN),
      ]);
      return { pool, days, events };
    },
    `pool:${code}`,
  );
}

export function useOverview(): Polled<Overview> {
  return usePolled(fetchOverview, "overview");
}
